import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, Scale, RefreshCcw, FileCheck2, MessageSquare, Handshake, PenLine, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import Seo from "@/components/site/Seo";
import Markdown from "@/components/site/Markdown";
import { PageHeader, Loading, Empty } from "@/components/site/Ui";
import { api, errMsg, useApi, formatDate } from "@/lib/api";
import { useSite } from "@/lib/site";

const METHOD = [
  [Search, "Source from official information", "We start with vendor websites, documentation and public product pages, and link to them so you can check for yourself."],
  [FileCheck2, "Verify pricing with a date", "Pricing is shown only after we check it on the official site, with the date it was last verified."],
  [Scale, "Evaluate against consistent criteria", "Features, ease of adoption, integrations, pricing transparency and fit for different business sizes."],
  [RefreshCcw, "Review and update", "Software changes quickly. We revisit profiles, note update dates and correct errors when readers report them."],
];

export function AboutPage() {
  return (
    <>
      <Seo title="About Us" description="SaaSPivora is a software discovery and research platform created to make business technology easier to understand." />
      <PageHeader eyebrow="About SaaSPivora" title="Helping Businesses Make Smarter Software Decisions." testId="about-header">
        SaaSPivora is a software discovery and research platform created to make business technology easier to understand. We publish software guides, comparisons, and practical resources designed to help organizations evaluate tools that match their needs, goals, and budgets.
      </PageHeader>
      <section className="container-x py-16 md:py-24" data-testid="about-methodology">
        <p className="eyebrow">How we research</p>
        <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold max-w-2xl">How software is researched and evaluated</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {METHOD.map(([Icon, t, d], i) => (
            <div key={t} className="card p-7 flex gap-5">
              <span className="font-display text-3xl font-extrabold text-slate-200">0{i + 1}</span>
              <div><h3 className="text-lg font-semibold flex items-center gap-2"><Icon className="h-5 w-5 text-[#3F5E7A]" />{t}</h3><p className="mt-2 text-sm leading-relaxed">{d}</p></div>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-[#F1F4F6] border-y border-[#DCE5E8]" data-testid="about-editorial-policy">
        <div className="container-x py-16 md:py-20 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="eyebrow">Transparency</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold">Our editorial policy</h2>
            <p className="mt-4">SaaSPivora may earn commissions from qualifying referrals. Affiliate relationships do not automatically determine our editorial recommendations, and pages with affiliate links are clearly labelled.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/legal/editorial-policy" className="btn-primary" data-testid="about-editorial-link">Read the editorial policy</Link>
              <Link to="/legal/affiliate-disclosure" className="btn-secondary" data-testid="about-affiliate-link">Affiliate disclosure</Link>
            </div>
          </div>
          <ul className="space-y-4">
            {["No fabricated ratings, testimonials or statistics", "Pricing shown with a last-verified date — or clearly marked as unverified", "Affiliate links disclosed and marked as sponsored", "Corrections welcomed through our contact page"].map((t) => (
              <li key={t} className="flex gap-3 card p-4"><CheckCircle2 className="h-5 w-5 text-[#0F766E] shrink-0" />{t}</li>
            ))}
          </ul>
        </div>
      </section>
      <AboutContact />
    </>
  );
}

function AboutContact() {
  const { settings } = useSite();
  return (
    <section className="container-x py-16 md:py-20" data-testid="about-contact">
      <div className="card p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <p className="eyebrow">Contact</p>
          <h2 className="mt-2 text-2xl font-bold">Questions, product suggestions or partnerships?</h2>
          <p className="mt-2 text-sm">Reach our team by email or through the contact form.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {settings.public_email && <a href={`mailto:${settings.public_email}`} className="btn-primary" data-testid="about-public-email">{settings.public_email}</a>}
          <Link to="/contact" className="btn-secondary" data-testid="about-contact-link">Contact form</Link>
        </div>
      </div>
    </section>
  );
}

const TYPES = [["general", "General inquiries", MessageSquare, "Questions about the site or our content."], ["partnership", "Business partnerships", Handshake, "Vendor, affiliate and partnership proposals."], ["editorial", "Editorial inquiries", PenLine, "Corrections, update requests and research questions."]];

export function ContactPage() {
  const { settings } = useSite();
  const [token, setToken] = useState("");
  const newToken = () => api.get("/contact/token").then((r) => setToken(r.data.token)).catch(() => {});
  useEffect(() => { newToken(); }, []);
  const [f, setF] = useState({ name: "", email: "", company: "", inquiry_type: "general", subject: "", message: "", website: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, error: "", done: false });
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Please enter a valid email address.";
    if (f.subject.trim().length < 3) e.subject = "Please add a subject.";
    if (f.message.trim().length < 10) e.message = "Your message should be at least 10 characters.";
    setErrors(e);
    return !Object.keys(e).length;
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setState({ busy: true, error: "", done: false });
    try {
      const r = await api.post("/contact", { ...f, form_token: token });
      setState({ busy: false, error: "", done: true, ref: r.data.ref });
      setTimeout(() => document.getElementById("contact-card")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    } catch (err) { setState({ busy: false, error: errMsg(err), done: false }); newToken(); }
  };
  const Field = ({ k, label, type = "text", ...rest }) => (
    <div>
      <label htmlFor={`c-${k}`} className="label">{label}</label>
      {type === "textarea" ? <textarea id={`c-${k}`} rows={6} value={f[k]} onChange={on(k)} className="input" data-testid={`contact-${k}-input`} {...rest} />
        : <input id={`c-${k}`} type={type} value={f[k]} onChange={on(k)} className="input" data-testid={`contact-${k}-input`} {...rest} />}
      {errors[k] && <p className="mt-1 text-xs text-rose-600" data-testid={`contact-${k}-error`}>{errors[k]}</p>}
    </div>
  );

  return (
    <>
      <Seo title="Contact" description="Contact SaaSPivora for general, partnership and editorial inquiries." />
      <PageHeader eyebrow="Contact" title="Get in touch" testId="contact-header">Choose the type of inquiry and we'll route your message to the right place.</PageHeader>
      <div className="container-x py-14 grid lg:grid-cols-[1fr_1.4fr] gap-10">
        <div className="space-y-4">
          {settings.public_email && (
            <div className="card p-6 border-[#BFE0DB] bg-[#F7FBFA]" data-testid="contact-enquiries-block">
              <h2 className="text-lg font-semibold">General Enquiries & Business Partnerships</h2>
              <p className="mt-2 text-sm leading-relaxed">Have a question about our software reviews, want to suggest a product, or interested in discussing a business partnership? We'd love to hear from you.</p>
              <p className="mt-4 text-sm font-medium text-[#17243A]">Email:{" "}
                <a href={`mailto:${settings.public_email}`} className="text-[#0F766E] font-semibold underline underline-offset-2 hover:text-[#17243A]" data-testid="contact-public-email">{settings.public_email}</a></p>
            </div>
          )}
          {TYPES.map(([v, t, Icon, d]) => (
            <button key={v} type="button" onClick={() => setF({ ...f, inquiry_type: v })} data-testid={`contact-type-${v}`}
              className={`card w-full text-left p-5 flex gap-4 ${f.inquiry_type === v ? "border-[#3F5E7A] ring-4 ring-[#DCE5E8]" : "card-hover"}`}>
              <Icon className="h-6 w-6 text-[#3F5E7A] shrink-0" /><div><p className="font-semibold text-[#17243A]">{t}</p><p className="text-sm text-[#64728A] mt-0.5">{d}</p></div>
            </button>
          ))}
          {settings.contact_email && settings.contact_email !== settings.public_email && <p className="text-sm px-1" data-testid="contact-email-display">Prefer email? <a href={`mailto:${settings.contact_email}`} className="text-[#3F5E7A] font-semibold">{settings.contact_email}</a></p>}
        </div>
        <div id="contact-card" className="card p-6 md:p-8">
          {state.done ? (
            <div className="py-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-[#0F766E] mx-auto" />
              <h2 className="mt-4 text-2xl font-bold">Message received</h2>
              <p className="mt-2">Thank you, {f.name.split(" ")[0]}. We've received your message and will reply to {f.email}.</p>
              {state.ref && <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#ECF6F4] border border-[#BFE0DB] px-4 py-1.5 text-sm text-[#17243A]" data-testid="contact-reference">Your reference: <strong className="font-mono">{state.ref}</strong></p>}
              <p className="mt-4 text-xs text-[#64728A]">Please keep this reference if you follow up by email.</p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">{Field({ k: "name", label: "Full name", autoComplete: "name" })}{Field({ k: "email", label: "Work email", type: "email", autoComplete: "email" })}</div>
              <div className="grid sm:grid-cols-2 gap-5">
                {Field({ k: "company", label: "Company (optional)", autoComplete: "organization" })}
                <div><label htmlFor="c-type" className="label">Inquiry type</label>
                  <select id="c-type" value={f.inquiry_type} onChange={on("inquiry_type")} className="input" data-testid="contact-type-select">
                    {TYPES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select></div>
              </div>
              {Field({ k: "subject", label: "Subject" })}
              {Field({ k: "message", label: "Message", type: "textarea" })}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={on("website")} className="hidden" aria-hidden="true" />
              <p className="text-xs text-[#64728A]">By sending this form you agree to our <Link to="/legal/privacy-policy" className="underline">Privacy Policy</Link>.</p>
              {state.error && <p role="alert" className="text-sm text-rose-600" data-testid="contact-error">{state.error}</p>}
              <button disabled={state.busy} className="btn-primary w-full sm:w-auto !px-8" data-testid="contact-submit-button">{state.busy && <Loader2 className="h-4 w-4 animate-spin" />}Send message</button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const { data, loading, error } = useApi(`/pages/${slug}`);
  if (loading) return <Loading />;
  if (error) return <NotFound />;
  return (
    <>
      <Seo title={data.seo_title || data.title} description={data.seo_description || `${data.title} for SaaSPivora.`} />
      <PageHeader eyebrow="Legal & transparency" title={data.title} testId="legal-header"><p className="text-sm text-[#64728A]">Last updated {formatDate(data.updated_at)}</p></PageHeader>
      <div className="container-x max-w-3xl py-12">
        {!data.owner_reviewed && (
          <div className="mb-8 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="legal-template-notice">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />This policy is a template pending owner review and may change.
          </div>
        )}
        <Markdown>{data.content}</Markdown>
      </div>
    </>
  );
}

export function NotFound() {
  return (
    <div className="container-x py-24" data-testid="not-found-page">
      <Seo title="Page not found" noindex />
      <Empty title="We couldn't find that page"><Link to="/" className="text-[#3F5E7A] underline">Return home</Link> or <Link to="/software" className="text-[#3F5E7A] underline">explore software</Link>.</Empty>
    </div>
  );
}
